<template>
  <div class="flex flex-wrap">
    <!-- Header Section -->
    <div class="w-full">
      <div class="px-4 pt-3 pb-3 bg-white rounded-lg shadow-xl overflow-hidden md:px-10 mx-auto w-full mb-6">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-4xl font-bold">Статус курсов</h1>
            <p class="text-blueGray-400 mt-2">Управление учениками и их статусом на курсах</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Card -->
    <div class="w-full px-4 mb-6">
      <div class="bg-white rounded-lg shadow-xl p-6">
        <h2 class="text-blueGray-800 text-lg font-bold mb-4">Фильтры</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-blueGray-600 text-xs uppercase font-bold mb-2">Статус</label>
            <select
              v-model="filters.status"
              @change="currentPage = 1; fetchStudents()"
              class="w-full px-3 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Все статусы</option>
              <option value="0">На рассмотрении</option>
              <option value="1">Одобрено</option>
              <option value="2">Отклонено</option>
            </select>
          </div>

          <div class="md:col-span-2">
            <label class="block text-blueGray-600 text-xs uppercase font-bold mb-2">Поиск</label>
            <input
              v-model="filters.search"
              @keyup.enter="currentPage = 1; fetchStudents()"
              placeholder="Поиск по ФИО или названию курса..."
              class="w-full px-3 py-2 border border-blueGray-300 rounded-lg text-blueGray-600 placeholder-blueGray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirmModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div class="p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-2">Подтверждение действия</h2>
          <p class="text-gray-600 mb-6">
            Вы уверены, что хотите <strong>{{ confirmAction === 'approve' ? 'одобрить' : 'отклонить' }}</strong> заявку?
          </p>
          <div v-if="pendingStudent" class="bg-gray-50 p-4 rounded mb-6">
            <p class="text-sm"><strong>Ученик:</strong> {{ pendingStudent.student.fio }}</p>
            <p class="text-sm"><strong>Курс:</strong> {{ pendingStudent.course }}</p>
          </div>
        </div>
        <div class="border-t p-6 flex justify-end gap-3">
          <button
            @click="showConfirmModal = false"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition font-semibold"
          >
            Отмена
          </button>
          <button
            @click="confirmAction === 'approve' ? confirmApprove() : confirmReject()"
            :class="confirmAction === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'"
            class="px-4 py-2 text-white rounded transition font-semibold"
          >
            {{ confirmAction === 'approve' ? 'Одобрить' : 'Отклонить' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Documents Modal -->
    <div v-if="showDocumentsModal" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h2 class="text-2xl font-bold text-gray-900">
            Документы: {{ selectedStudent?.student?.fio }}
          </h2>
          <button
            @click="showDocumentsModal = false"
            class="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <div class="p-6 space-y-4">
          <!-- IIN -->
          <div class="border-b pb-4">
            <h3 class="font-semibold text-gray-800 mb-2">ИИН</h3>
            <p class="text-gray-600">{{ selectedStudent?.documents?.iin || 'Не указан' }}</p>
          </div>

          <!-- Criminal Record Certificate -->
          <div class="border-b pb-4">
            <h3 class="font-semibold text-gray-800 mb-2">Справка о несудимости</h3>
            <a
              v-if="selectedStudent?.documents?.criminal_record_cert"
              :href="selectedStudent.documents.criminal_record_cert"
              target="_blank"
              class="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded hover:bg-red-100 transition"
            >
              📄 Скачать PDF
            </a>
            <p v-else class="text-gray-500">Не загружено</p>
          </div>

          <!-- Narcology Certificate -->
          <div class="border-b pb-4">
            <h3 class="font-semibold text-gray-800 mb-2">Справка из наркологического диспансера</h3>
            <a
              v-if="selectedStudent?.documents?.narcology_cert"
              :href="selectedStudent.documents.narcology_cert"
              target="_blank"
              class="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 border border-orange-200 text-orange-700 rounded hover:bg-orange-100 transition"
            >
              📄 Скачать PDF
            </a>
            <p v-else class="text-gray-500">Не загружено</p>
          </div>

          <!-- Psychiatry Certificate -->
          <div class="border-b pb-4">
            <h3 class="font-semibold text-gray-800 mb-2">Справка из психоневрологического диспансера</h3>
            <a
              v-if="selectedStudent?.documents?.psychiatry_cert"
              :href="selectedStudent.documents.psychiatry_cert"
              target="_blank"
              class="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded hover:bg-yellow-100 transition"
            >
              📄 Скачать PDF
            </a>
            <p v-else class="text-gray-500">Не загружено</p>
          </div>

          <!-- ID Copy -->
          <div>
            <h3 class="font-semibold text-gray-800 mb-2">Копия удостоверения личности</h3>
            <a
              v-if="selectedStudent?.documents?.id_copy"
              :href="selectedStudent.documents.id_copy"
              target="_blank"
              class="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 text-green-700 rounded hover:bg-green-100 transition"
            >
              📄 Скачать PDF
            </a>
            <p v-else class="text-gray-500">Не загружено</p>
          </div>
        </div>

        <div class="border-t p-6 flex justify-end">
          <button
            @click="showDocumentsModal = false"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>

    <div class="w-full lg:w-full px-4">
      <div
        style="min-height: calc(100vh - 176px)"
        class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0"
      >
        <div
          style="max-height: 100%; overflow: hidden"
          class="mt-5 relative overflow-x-auto shadow-md sm:rounded-lg"
        >

          <div class="table-container inl-100 tsor-table">
            <div class="table-row header">
              <div class="row-item inl-20 bt-n bb-n br-r">
                <span class="px-6 py-2">ФИО</span>
              </div>
              <div class="row-item inl-20 bt-n bb-n br-r">
                <span class="px-6 py-2">Курс</span>
              </div>
              <div class="row-item inl-20 bt-n bb-n br-r">
                <span class="px-6 py-2">Статус</span>
              </div>
              <div class="row-item inl-20 bt-n bb-n br-r">
                <span class="px-6 py-2">Дата подачи</span>
              </div>
              <div class="row-item inl-20 bt-n bb-n br-r">
                <span class="px-6 py-2">Дата одобрения</span>
              </div>
              <div class="row-item inl-20 bt-n bb-n br-r">
                <span class="px-6 py-2">Попытки</span>
              </div>
              <div
                style="justify-content: center"
                class="row-item inl-20 bt-n bb-n br-r"
              >
                <span class="px-6 py-2 center text-center">Действие</span>
              </div>
            </div>

            <div
              v-for="(el, ind) in courses"
              :key="ind"
              class="table-row body"
              :style="el.is_archived ? 'opacity: 0.5; background: #f9fafb;' : ''"
            >
              <div class="row-item inl-20">
                <span class="px-6 py-2">{{ el.student.fio }}</span>
              </div>
              <div class="row-item inl-20">
                <span class="px-6 py-2">{{ el.course }}</span>
              </div>
              <div class="row-item inl-20">
                <span class="px-6 py-2">{{ el.status.val }}</span>
                <span
                  v-if="el.is_archived"
                  class="ml-1 px-1 py-0.5 bg-gray-300 text-gray-600 rounded text-xs"
                >Архив</span>
              </div>
              <div class="row-item inl-20">
                <span class="px-6 py-2">{{ formatDate(el.created_at) }}</span>
              </div>
              <div class="row-item inl-20">
                <span class="px-6 py-2">{{ formatDate(el.approved_at) }}</span>
              </div>
              <div class="row-item inl-20">
                <span class="px-6 py-2">{{ el.attempt_number }} / {{ el.max_attempts }}</span>
                <button
                  v-if="!el.is_archived && el.attempt_number >= el.max_attempts && (el.status.key !== 1 || el.is_blocked)"
                  @click="resetAttempts(el.id)"
                  class="ml-2 px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600 transition"
                  title="Сбросить все попытки и начать цикл заново"
                >
                  Сбросить
                </button>
              </div>
              <div class="row-item inl-20">
                <div class="flex-center inl-100 m-auto center gap-2">
                  <button
                    @click="viewDocuments(el)"
                    title="Просмотр документов"
                    class="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition"
                  >
                    📄
                  </button>
                  <div v-if="!el.is_archived && el.status.key == 0" class="flex-center gap-1">
                    <div
                      @click="setStatus(el.id, ind, true)"
                      style="justify-content: center"
                      class="inl-45 flex-center"
                      title="Одобрить"
                    >
                      <svg
                        style="color: #059669"
                        class="w-6 h-6"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.546.5a9.5 9.5 0 1 0 9.5 9.5 9.51 9.51 0 0 0-9.5-9.5ZM13.788 11h-3.242v3.242a1 1 0 1 1-2 0V11H5.304a1 1 0 0 1 0-2h3.242V5.758a1 1 0 0 1 2 0V9h3.242a1 1 0 1 1 0 2Z"
                        />
                      </svg>
                    </div>
                    <div
                      @click="setStatus(el.id, ind, false)"
                      style="justify-content: center"
                      class="inl-45 flex-center"
                      title="Отклонить"
                    >
                      <svg
                        class="w-6 h-6 text-red-600"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-6 flex justify-center items-center gap-4 px-4">
        <button
          @click="currentPage = Math.max(1, currentPage - 1); fetchStudents()"
          :disabled="currentPage === 1"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 transition"
        >
          ← Назад
        </button>
        <span class="px-4 py-2 text-gray-700">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button
          @click="currentPage = Math.min(totalPages, currentPage + 1); fetchStudents()"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 disabled:opacity-50 transition"
        >
          Вперед →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_CLIENT, HEADERS_TOKEN_JSON, DEFAULT_PER_PAGE } from "@/const/uri";

export default {
  data() {
    return {
      courses: [],
      showDocumentsModal: false,
      showConfirmModal: false,
      selectedStudent: null,
      pendingStudent: null,
      pendingIndex: null,
      confirmAction: null,
      currentPage: 1,
      totalPages: 1,
      perPage: DEFAULT_PER_PAGE,
      filters: {
        status: '',
        search: ''
      }
    };
  },
  mounted() {
    this.fetchStudents();
  },
  methods: {
    viewDocuments(student) {
      this.selectedStudent = student;
      this.showDocumentsModal = true;
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },
    changeData(index, data) {
      this.courses[index].status = data;
      if (data.approved_at) {
        this.courses[index].approved_at = data.approved_at;
      }
    },
    setStatus(id, index, status) {
      this.pendingStudent = this.courses[index];
      this.pendingIndex = index;
      this.confirmAction = status ? 'approve' : 'reject';
      this.showConfirmModal = true;
    },
    async confirmApprove() {
      if (this.pendingStudent) {
        await this.executeStatus(this.pendingStudent.id, this.pendingIndex, true);
      }
    },
    async confirmReject() {
      if (this.pendingStudent) {
        await this.executeStatus(this.pendingStudent.id, this.pendingIndex, false);
      }
    },
    async executeStatus(id, index, status) {
      try {
        const response = await axios({
          method: "POST",
          url: API_CLIENT.moder + id + "/status/",
          data: {
            status,
          },
          headers: HEADERS_TOKEN_JSON(),
        });
        const data = response.data.data;
        this.changeData(index, data.status);
        await this.fetchStudents();
        this.showConfirmModal = false;
        this.pendingStudent = null;
        this.pendingIndex = null;
        this.confirmAction = null;
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          alert(error.response.data.error);
        } else {
          console.error('Error setting status:', error);
        }
      }
    },
    async resetAttempts(id) {
      try {
        await axios({
          method: "POST",
          url: API_CLIENT.moder + id + "/reset_attempts/",
          headers: HEADERS_TOKEN_JSON(),
        });
        await this.fetchStudents();
      } catch (error) {
        console.error('Error resetting attempts:', error);
      }
    },
    async fetchStudents() {
      try {
        const params = {
          p: this.currentPage,
          page_size: this.perPage
        };

        if (this.filters.status) {
          params.status = this.filters.status;
        }

        if (this.filters.search) {
          params.search = this.filters.search;
        }

        const response = await axios({
          method: "GET",
          url: API_CLIENT.moder,
          params,
          headers: HEADERS_TOKEN_JSON(),
        });

        const data = response.data.data;
        if (Array.isArray(data)) {
          this.courses = data;
          this.totalPages = 1;
        } else if (data.results) {
          this.courses = data.results;
          this.totalPages = Math.ceil((data.count || 0) / this.perPage);
        } else {
          this.courses = data;
        }
      } catch (error) {
        console.error('Error fetching students:', error);
      }
    },
  },
};
</script>